import { SourceType } from '../api/SourceApi';
import { PostingType } from '../data/models/Posting';
import translate from '../language/translate';

export const DeeplinkKey = 'deeplink';

export enum InputState {
  EDITABLE,
  DISABLED,
}

export enum ApportionmentType {
  CROSS_ENTITY = 'crossentity',
  INTRA_ENTITY = 'intraentity',
  ASSIGNMENT = 'assignment',
}

export enum ApportionmentUnit {
  UNIT = 'unit',
  PERCENTAGE = 'percent',
}

export enum ApportionmentVisibility {
  GROUP = 'group',
  RULE = 'rule',
}

export enum ApportionmentAssignmentAction {
  RETAIN = 'retain',
  SET = 'set',
}

export enum NavigationState {
  GROUPS, PRODUCTS, PAGES,
}

export enum Products {
  RECHARGER = 'recharger', LOAN_ACCOUNT = 'balancer', BRAG = 'brag',
}

export enum RuleTransactionType {
  expense = 'expense',
  income = 'revenue',
  balancesheet = 'balance sheet',
  none = 'none', 
}

export enum PostingLineGrouping {
  rule = 'rule',
  transaction = 'transaction',
}

export enum FixedAmountApplicationType {
  total = 'total',
  unit = 'unit',
}

export const FixedAmountApplicationTypeOptions = [
  { id: FixedAmountApplicationType.total, name: translate('CreateRule.Mayday.APPLICATION_TOTAL') },
  { id: FixedAmountApplicationType.unit, name: translate('CreateRule.Mayday.APPLICATION_UNIT') },
];

export enum TxType {
  INCOME = 'income',
  EXPENSE = 'expense',
  BALANCESHEET = 'balancesheet',
  ASSET = 'asset',
  LIABILITY = 'liability',
}

export enum TransactionsGrouping {
  NOMINAL_CODE,
  RULE,
}

export enum TransactionPageType {
  TRANSACTIONS,
  RECHARGE_TRANSACTIONS,
}

export enum BillingFrequency {
  ANNUAL = 'annual', MONTHLY = 'monthly',
}

export type InvoiceEntities = {
  from: number,
  to: number,
};

export enum InvoiceType {
  SALES = 'sales',
  PURCHASE = 'purchase',
}

export enum RelatedEntityType {
  CALCULATION = 'calculation',
  LOAN = 'loan',
}

export enum LoanTransactionType {
  LOAN = 'loan',
  REPAYMENT = 'repayment',
  INTEREST = 'interest',
  FX = 'fx_movement',
  UNKNOWN = 'unknown',
  CONVERSION_BALANCE = 'conversionBalance',
  REVERSAL = 'reversal',
  RECHARGE = 'recharge',
  BRAG = 'brag',
}

export enum LoanErrorType {
  NONE = 'none',
  FX = 'fx_movement',
  TX_MISSING = 'tx_missing',
  UNKNOWN = 'unknown',
}

export enum UserType {
  USER = 'user',
  SUPPORT = 'support',
}

export enum ExchangeRateRange {
  DAY = 'day',
  MONTH = 'month',
  ALL = 'all',
}

export enum InterestRateRange {
  NO_INTEREST = 'no_interest',
  FIXED_RATE = 'fixed_rate',
  VARIABLE = 'variable',
}

export enum RuleType {
  NONE = 'none',
  COST = 'cost',
  COST_PLUS = 'costPlus',
  SALES = 'sales',
  NONE_SALES = 'noneSales',
  VALUE = 'value',
  NONE_BALANCE = 'noneBalance',
  FIXED = 'fixed',
}

export enum MatchType {
  NOMINAL_CODE = 'nominalCode',
  TRACKING_CATEGORY = 'trackingCategory',
  CONTACT = 'contact',
  LINKED_CONTACT = 'linkedContact',
  DESCRIPTION = 'description',
  NARRATION = 'narration',
}

export enum MatchCondition {
  ONE_OF = 'oneOf',
  NOT_ONE_OF = 'notOneOf',
  CONTAINS = 'contains',
  NOT_CONTAIN = 'doesNotContain',
}

export enum BooleanCondition {
  ALL = 'ALL',
  ANY = 'ANY',
}

export enum CostHandler {
  NET = 'net',
  CODE = 'code',
}

export const ExpenseRuleTypes = new Map([
  [RuleType.COST, translate('Rules.Types.cost')],
  [RuleType.COST_PLUS, translate('Rules.Types.costPlus')],
  [RuleType.NONE, translate('Rules.Types.none')],
]);

export const SalesRuleTypes = new Map([
  [RuleType.SALES, translate('Rules.Types.sales')],
  [RuleType.NONE_SALES, translate('Rules.Types.noneSales')],
]);

export const BalanceSheetRuleTypes = new Map([
  [RuleType.VALUE, translate('Rules.Types.value')],
  [RuleType.NONE_BALANCE, translate('Rules.Types.noneBalance')],
]);

export const StringMatchConditions = new Map([
  [MatchCondition.CONTAINS, translate('Rules.MatchConditions.contains')],
  [MatchCondition.NOT_CONTAIN, translate('Rules.MatchConditions.doesNotContain')],
]);

export const ValueMatchConditions = new Map([
  [MatchCondition.ONE_OF, translate('Rules.MatchConditions.oneOf')],
  [MatchCondition.NOT_ONE_OF, translate('Rules.MatchConditions.notOneOf')],
]);

export const BooleanConditions = new Map([
  [BooleanCondition.ALL, translate('Rules.BooleanConditions.ALL')],
  [BooleanCondition.ANY, translate('Rules.BooleanConditions.ANY')],
]);

export const XeroMatchTypes = new Map([
  [MatchType.TRACKING_CATEGORY, translate('Rules.MatchTypes.trackingCategory')],
  [MatchType.NOMINAL_CODE, translate('Rules.MatchTypes.nominalCode')],
  [MatchType.CONTACT, translate('Rules.MatchTypes.contact')],
  [MatchType.LINKED_CONTACT, translate('Rules.MatchTypes.linkedContact')],
  [MatchType.NARRATION, translate('Rules.MatchTypes.narration')],
  [MatchType.DESCRIPTION, translate('Rules.MatchTypes.description')],
]);

export const GoogleSheetsMatchTypes = new Map([
  [MatchType.TRACKING_CATEGORY, translate('Rules.MatchTypes.trackingCategory')],
  [MatchType.NOMINAL_CODE, translate('Rules.MatchTypes.nominalCode')],
  [MatchType.DESCRIPTION, translate('Rules.MatchTypes.description')],
]);

export const RuleSourceOptions = new Map([
  [SourceType.xero, translate('Sources.Types.xero')],
  [SourceType.googleSheets, translate('Sources.Types.googleSheets')],
]);

export const ApportionmentUnits = new Map([
  [ApportionmentUnit.UNIT, 'Units'],
  [ApportionmentUnit.PERCENTAGE, 'Percentage'],
]);

export const LoanTransactionTypeOptions = [
  [LoanTransactionType.LOAN, translate('Loans.TransactionTypes.loan')],
  [LoanTransactionType.REPAYMENT, translate('Loans.TransactionTypes.repayment')],
  [LoanTransactionType.INTEREST, translate('Loans.TransactionTypes.interest')],
  [LoanTransactionType.FX, translate('Loans.TransactionTypes.fx_movement')],
  [LoanTransactionType.BRAG, translate('Loans.TransactionTypes.brag')],
  [LoanTransactionType.RECHARGE, translate('Loans.TransactionTypes.recharge')],
  [LoanTransactionType.REVERSAL, translate('Loans.TransactionTypes.reversal')],
  [LoanTransactionType.UNKNOWN, translate('Loans.TransactionTypes.unknown')],
];

export const PostingTypeOptions = [
  { id: PostingType.INVOICE, name: 'Invoice & Bill' },
  { id: PostingType.JOURNAL, name: 'Journals' },
];

export const DoNotRechargeId = -1000;
export const CustomApportionmentId = -1;

